import React, { useMemo } from "react";
import {
  ComposedChart,
  LineChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const DURATION_COLOR = "#2a78d6";
const ANSWERED_COLOR = "#2a78d6";
const CORRECT_COLOR = "#0ca30c";
const WRONG_COLOR = "#d03b3b";
const OTHER_COLOR = "#898781";
const SUBJECT_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300"];

const GRID = "#e1e0d9";
const AXIS_LINE = "#c3c2b7";
const MUTED = "#898781";
const SURFACE = "#fcfcfb";

const formatDateTick = (dateIST) => {
  const [, m, d] = dateIST.split("-");
  return `${d}/${m}`;
};

function buildDurationSeries(data) {
  const minutes = data.map((d) => Math.round((d.durationSeconds / 60) * 10) / 10);
  const real = data.map((d, i) => (d.durationIsApproximate ? null : minutes[i]));
  const approx = data.map((d, i) => (d.durationIsApproximate ? minutes[i] : null));
  for (let i = 1; i < data.length; i++) {
    const wasApprox = data[i - 1].durationIsApproximate;
    const isApprox = data[i].durationIsApproximate;
    if (wasApprox !== isApprox) {
      if (wasApprox && !isApprox) {
        approx[i] = minutes[i];
        real[i - 1] = minutes[i - 1];
      } else {
        real[i] = minutes[i];
        approx[i - 1] = minutes[i - 1];
      }
    }
  }
  return { real, approx };
}

const DurationTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload.find((p) => p.value !== null && p.value !== undefined);
  if (!point) return null;
  const isApprox = point.dataKey === "durationApprox";
  return (
    <div className="dash-tooltip">
      <div className="dash-tooltip-label">{label}</div>
      <div className="dash-tooltip-row">
        <span className="dash-tooltip-key" style={{ background: DURATION_COLOR }} />
        <span className="dash-tooltip-value">{point.value} min</span>
        <span className="dash-tooltip-name">{isApprox ? "approximate" : "tracked"}</span>
      </div>
    </div>
  );
};

const GenericTooltip = ({ active, payload, label, nameMap }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="dash-tooltip">
      <div className="dash-tooltip-label">{label}</div>
      {payload.map((p) => (
        <div className="dash-tooltip-row" key={p.dataKey}>
          <span className="dash-tooltip-key" style={{ background: p.color }} />
          <span className="dash-tooltip-value">{p.value}</span>
          <span className="dash-tooltip-name">{nameMap?.[p.dataKey] || p.dataKey}</span>
        </div>
      ))}
    </div>
  );
};

const HoursTrendChart = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data || !data.length) return [];
    const { real, approx } = buildDurationSeries(data);
    return data.map((d, i) => ({
      date: formatDateTick(d.dateIST),
      dateIST: d.dateIST,
      durationReal: real[i],
      durationApprox: approx[i],
      durationIsApproximate: d.durationIsApproximate,
    }));
  }, [data]);

  const hasAnyApprox = chartData.some((d) => d.durationIsApproximate);

  if (!chartData.length) return <div className="dash-section-empty">No data in this range.</div>;

  return (
    <div className="dash-chart-panel">
      <div className="dash-chart-panel-header">
        <h3 className="dash-chart-panel-title">Hours spent on app</h3>
        {hasAnyApprox && (
          <div className="dash-chart-caption">
            <span className="dash-caption-swatch dash-caption-solid" /> tracked
            <span className="dash-caption-swatch dash-caption-dashed" /> approximate
          </div>
        )}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} />
          <XAxis dataKey="date" tick={{ fill: MUTED, fontSize: 11 }} axisLine={{ stroke: AXIS_LINE }} tickLine={false} />
          <YAxis
            tick={{ fill: MUTED, fontSize: 11 }}
            axisLine={{ stroke: AXIS_LINE }}
            tickLine={false}
            width={40}
            label={{ value: "minutes", angle: -90, position: "insideLeft", fill: MUTED, fontSize: 11 }}
          />
          <Tooltip content={<DurationTooltip />} />
          <Area
            type="monotone" dataKey="durationReal" stroke={DURATION_COLOR} strokeWidth={2}
            fill={DURATION_COLOR} fillOpacity={0.1} connectNulls={false}
            dot={{ r: 4, fill: DURATION_COLOR, stroke: SURFACE, strokeWidth: 2 }} isAnimationActive={false}
          />
          <Area
            type="monotone" dataKey="durationApprox" stroke={DURATION_COLOR} strokeWidth={2} strokeDasharray="4 3"
            fill={DURATION_COLOR} fillOpacity={0.04} connectNulls={false}
            dot={{ r: 4, fill: SURFACE, stroke: DURATION_COLOR, strokeWidth: 2 }} isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <p className="perf-chart-note">Not affected by the Subject filter — app-usage time isn't tracked per subject.</p>
    </div>
  );
};

const QUESTIONS_NAME_MAP = { answered: "Answered", correct: "Correct", wrong: "Wrong" };

const QuestionsTrendChart = ({ data }) => {
  const chartData = useMemo(
    () => (data || []).map((d) => ({ date: formatDateTick(d.dateIST), dateIST: d.dateIST, answered: d.answered, correct: d.correct, wrong: d.wrong })),
    [data]
  );

  if (!chartData.length) return <div className="dash-section-empty">No data in this range.</div>;

  return (
    <div className="dash-chart-panel">
      <div className="dash-chart-panel-header">
        <h3 className="dash-chart-panel-title">Questions answered — total / correct / wrong</h3>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} />
          <XAxis dataKey="date" tick={{ fill: MUTED, fontSize: 11 }} axisLine={{ stroke: AXIS_LINE }} tickLine={false} />
          <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={{ stroke: AXIS_LINE }} tickLine={false} width={36} allowDecimals={false} />
          <Tooltip content={<GenericTooltip nameMap={QUESTIONS_NAME_MAP} />} />
          <Legend wrapperStyle={{ fontSize: 12, color: "#52514e" }} formatter={(value) => QUESTIONS_NAME_MAP[value] || value} />
          <Line type="monotone" dataKey="answered" stroke={ANSWERED_COLOR} strokeWidth={2}
            dot={{ r: 4, fill: ANSWERED_COLOR, stroke: SURFACE, strokeWidth: 2 }} isAnimationActive={false} />
          <Line type="monotone" dataKey="correct" stroke={CORRECT_COLOR} strokeWidth={2}
            dot={{ r: 4, fill: CORRECT_COLOR, stroke: SURFACE, strokeWidth: 2 }} isAnimationActive={false} />
          <Line type="monotone" dataKey="wrong" stroke={WRONG_COLOR} strokeWidth={2}
            dot={{ r: 4, fill: WRONG_COLOR, stroke: SURFACE, strokeWidth: 2 }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const NotesBySubjectChart = ({ data, subjectSeries }) => {
  const colorBySubject = useMemo(() => {
    const map = {};
    let slot = 0;
    (subjectSeries || []).forEach((name) => {
      if (name === "Other") {
        map[name] = OTHER_COLOR;
      } else {
        map[name] = SUBJECT_COLORS[slot % SUBJECT_COLORS.length];
        slot += 1;
      }
    });
    return map;
  }, [subjectSeries]);

  const chartData = useMemo(
    () =>
      (data || []).map((d) => {
        const row = { date: formatDateTick(d.dateIST), dateIST: d.dateIST };
        (subjectSeries || []).forEach((name) => {
          row[name] = d.bySubject?.[name] || 0;
        });
        return row;
      }),
    [data, subjectSeries]
  );

  if (!chartData.length || !subjectSeries?.length) {
    return <div className="dash-section-empty">No notes completed in this range.</div>;
  }

  return (
    <div className="dash-chart-panel">
      <div className="dash-chart-panel-header">
        <h3 className="dash-chart-panel-title">Notes completed, by subject</h3>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} />
          <XAxis dataKey="date" tick={{ fill: MUTED, fontSize: 11 }} axisLine={{ stroke: AXIS_LINE }} tickLine={false} />
          <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={{ stroke: AXIS_LINE }} tickLine={false} width={36} allowDecimals={false} />
          <Tooltip content={<GenericTooltip nameMap={Object.fromEntries((subjectSeries || []).map((s) => [s, s]))} />} />
          <Legend wrapperStyle={{ fontSize: 12, color: "#52514e" }} />
          {(subjectSeries || []).map((name) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={colorBySubject[name]}
              strokeWidth={2}
              dot={{ r: 4, fill: colorBySubject[name], stroke: SURFACE, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const PerformanceCharts = ({ hoursTrend, questionsTrend, notesBySubjectTrend, subjectSeries, loading, error }) => {
  if (error) {
    return <div className="dash-section-error">Couldn't load performance charts: {error}</div>;
  }
  if (loading) {
    return <div className="dash-chart-skeleton" />;
  }
  return (
    <div className="dash-activity-charts">
      <HoursTrendChart data={hoursTrend} />
      <QuestionsTrendChart data={questionsTrend} />
      <NotesBySubjectChart data={notesBySubjectTrend} subjectSeries={subjectSeries} />
    </div>
  );
};

export default PerformanceCharts;
